import React, { useState } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { colors } from '../../theme/colors';
import SlideHeader from './SlideHeader';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import HandshakeIcon from '@mui/icons-material/Handshake';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BalanceIcon from '@mui/icons-material/Balance';

const steps = [
  {
    label: 'Program Enrollment',
    description: 'Complete enrollment forms and provide necessary documentation to begin the program.',
    icon: <AssignmentIcon />
  },
  {
    label: 'Dedicated Account Setup',
    description: 'Set up a dedicated account for your monthly program deposits.',
    icon: <AccountBalanceWalletIcon />
  },
  {
    label: 'Monthly Deposits',
    description: 'Make regular monthly deposits into your dedicated account while we negotiate with creditors.',
    icon: <SavingsIcon />
  },
  {
    label: 'Creditor Negotiations',
    description: 'Our team negotiates with creditors to reduce your total debt amount.',
    icon: <HandshakeIcon />
  },
  {
    label: 'Settlement Agreements',
    description: 'Review and approve settlement offers as they become available.',
    icon: <TaskAltIcon />
  },
  {
    label: 'Debt Resolution',
    description: 'Settlements are paid from your dedicated account, resolving your debts one by one.',
    icon: <BalanceIcon />
  }
];

const LogisticsSlide: React.FC = () => {
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);

  const handleStepHover = (index: number | null) => {
    setHoveredStep(index);
  };

  return (
    <Box>
      <SlideHeader title="Program Process" />
      {/* Content Section */}
      <Box sx={{ 
        px: 3, 
        pb: 3,
        position: 'relative',
      }}>
        <Box
          sx={{
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Grid container spacing={2}>
            {steps.map((step, index) => (
              <Grid 
                item 
                xs={12} 
                md={6} 
                key={index}
                sx={{
                  position: 'relative',
                }}
              >
                <Paper
                  elevation={hoveredStep === index ? 12 : 1}
                  onMouseEnter={() => handleStepHover(index)}
                  onMouseLeave={() => handleStepHover(null)}
                  sx={{
                    p: 2,
                    height: '100%',
                    cursor: 'pointer',
                    borderLeft: `4px solid ${colors.primary.main}`,
                    bgcolor: '#fff',
                    position: 'relative',
                    transition: 'all 0.3s ease-in-out',
                    opacity: hoveredStep === null ? 1 : hoveredStep === index ? 1 : 0.2,
                    transform: hoveredStep === index ? 'scale(1.05)' : 'none',
                    transformOrigin: 'center center',
                    zIndex: hoveredStep === index ? 99999 : 1,
                    boxShadow: hoveredStep === index ? '0 8px 16px rgba(0,0,0,0.15)' : 'none',
                    '&:hover': {
                      bgcolor: '#fff',
                    }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 1,
                  }}>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        color: colors.primary.main,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.3s ease-in-out',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mr: 2,
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.5rem',
                            color: colors.primary.main,
                          }
                        }}
                      >
                        {step.icon}
                      </Box>
                      {step.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.95rem',
                      transition: 'all 0.3s ease-in-out',
                      ...(hoveredStep === index && {
                        color: 'text.primary',
                      }),
                    }}
                  >
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Grid item xs={12}>
          <Paper sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6" gutterBottom color={colors.primary.main}>
              Important Notes:
            </Typography>
            <Typography variant="body1" paragraph>
              • The program typically resolves debts within 36-60 months, depending on your chosen term.
            </Typography>
            <Typography variant="body1" paragraph>
              • Settlement offers can begin as soon as sufficient funds are available in your dedicated account.
            </Typography>
            <Typography variant="body1" paragraph>
              • Our team provides regular updates on your program progress and available settlement opportunities.
            </Typography>
            <Typography variant="body1">
              • Customer support is available throughout your program to answer questions and provide assistance.
            </Typography>
          </Paper>
        </Grid>
      </Box>
    </Box>
  );
};

export default LogisticsSlide;
